import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  createPartner,
  deletePartner,
  getPartner,
  listPartners,
  updatePartner,
} from "@/services/portalApi"
import type { Partner } from "@/services/types"

export const partnerQueryKeys = {
  all: ["partners"] as const,
  detail: (id: string) => [...partnerQueryKeys.all, "detail", id] as const,
}

export function usePartners(name?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: partnerQueryKeys.all,

    enabled: options?.enabled ?? true,

    staleTime: 1000 * 60 * 5,

    queryFn: async () => {
      let partners: Array<Partner> = []
      const pagination = { offset: 0, limit: 200 }

      while (true) {
        const response = await listPartners(pagination)
        if (response.length === 0) {
          break
        }
        partners = partners.concat(response)
        if (response.length < pagination.limit) {
          break
        }
        pagination.offset += response.length
      }

      return partners
    },

    select: (partners) =>
      partners.filter((p) => {
        // If name is provided, only return partners that include the search value in the name
        if (name) {
          return p.name.toLowerCase().includes(name.toLowerCase())
        }

        // If no filters are provided, return all partners
        return true
      }),
  })
}

export function usePartner(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: partnerQueryKeys.detail(id),

    queryFn: () => getPartner(id),

    initialData: () => {
      // The list no longer carries counts; seed with 0s until getPartner refetches.
      const cached = queryClient
        .getQueryData<Array<Partner>>(partnerQueryKeys.all)
        ?.find((s) => s.id === id)
      return cached
        ? {
            ...cached,
            subscriber_count: 0,
            device_count: 0,
          }
        : undefined
    },

    initialDataUpdatedAt: () =>
      queryClient.getQueryState(partnerQueryKeys.all)?.dataUpdatedAt,
  })
}

export function useCreatePartner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createPartner,

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: partnerQueryKeys.all })
      const previous = queryClient.getQueryData(partnerQueryKeys.all)
      queryClient.setQueryData<Array<Partner>>(partnerQueryKeys.all, (old) => {
        const partner = {
          ...payload,
          id: "new",
        } as Partner
        return old ? [...old, partner] : [partner]
      })
      return { previous }
    },

    onError: (_err, _newSubscriber, context) => {
      if (context) {
        queryClient.setQueryData(partnerQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}

export function useUpdatePartner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updatePartner,

    onMutate: async ({ partnerId, payload }) => {
      await queryClient.cancelQueries({ queryKey: partnerQueryKeys.all })
      const previous = queryClient.getQueryData(partnerQueryKeys.all)
      queryClient.setQueryData<Array<Partner>>(partnerQueryKeys.all, (old) => {
        return old?.map((p) => (p.id === partnerId ? { ...p, ...payload } : p))
      })
      return { previous }
    },

    onError: (_err, _newSubscriber, context) => {
      if (context) {
        queryClient.setQueryData(partnerQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}

export function useDeletePartner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deletePartner,

    onMutate: async (partnerId) => {
      await queryClient.cancelQueries({ queryKey: partnerQueryKeys.all })
      const previous = queryClient.getQueryData(partnerQueryKeys.all)
      queryClient.setQueryData<Array<Partner>>(partnerQueryKeys.all, (old) => {
        return old?.filter(({ id }) => id !== partnerId)
      })
      return { previous }
    },

    onError: (_err, _subscriber, context) => {
      if (context) {
        queryClient.setQueryData(partnerQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}
